import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import { useNavigate } from "react-router-dom";
import { useEmployment } from "app/hooks/employment/useEmployment";
import { EMPLOYMENT } from "app/constants/apiEndPoints";
import moment from "moment";
import toast from "react-hot-toast";
import {
  nextStep,
  setStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointemploymentDetails";

import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";
import { JointemploymentDetailsFromFour } from "app/utils/types";
import DisabledSecondEmploymentDetails from "./DisabledSecondEmploymentDetails";
import DynamicSecondEmployment from "app/components/Individual/employmentDetails/dynamicForms/DynamicSecondEmployment";

const SecondEmployment: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync: submitEmployment, isPending } = useEmployment();
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const { formFour, ...others } = useSelector(
    (state: RootState) => state.jointemploymentDetails.stepsData
  );

  const formTwoData = others?.formTwo;
  const { coBorrowerDetails, yourDetails } = formTwoData || {};

  const isYourPreviousEmployer = yourDetails?.previousemployer === "yes";
  const isCoBorrowerPreviousEmployer =
    coBorrowerDetails?.previousemployer === "yes";

  // ---- have the second job ----
  const formThreeData = others?.formThree;

  const isYourSecondJob =
    formThreeData?.yourDetails?.secondjob === "yes" ||
    formTwoData?.yourDetails?.hassecondjob === "yes";

  const isCoBorrowerSecondJob =
    formThreeData?.coBorrowerDetails?.secondjob === "yes" ||
    formTwoData?.coBorrowerDetails?.hassecondjob === "yes";

  // ---- end have the second job ----

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
    register,
    reset,
    setError,
    watch,
    unregister,
  } = useForm<JointemploymentDetailsFromFour>({
    defaultValues: {
      yourDetails: {
        employerPhoneNumber: "",
        postCode: "",
        state: "",
      },
      coBorrowerDetails: {
        employerPhoneNumber: "",
        postCode: "",
        state: "",
      },
    },
    shouldUnregister: true,
  });

  const yourEmploymentType = watch("yourDetails.employmentType");
  const coEmploymentType = watch("coBorrowerDetails.employmentType");

  const onSubmit = async (data: JointemploymentDetailsFromFour) => {
    const { yourDetails, coBorrowerDetails } = data || {};

    const yourDetailsData = {
      employmentType: others?.formTwo?.yourDetails?.employmentType,
      joiningDate: moment(others?.formTwo?.yourDetails?.joiningDate).format(
        "YYYY-MM-DD"
      ),
      employerName: others?.formOne?.yourDetails?.employerName ?? "",
      ...(formTwoData?.yourDetails?.hassecondjob === "no" && {
        previousEmployer: {
          ...others?.formThree?.yourDetails,
          startDate: moment(others?.formThree?.yourDetails?.startDate).format(
            "YYYY-MM-DD"
          ),
          endDate: moment(others?.formThree?.yourDetails?.endDate).format(
            "YYYY-MM-DD"
          ),
        },
      }),

      secondJob: {
        ...yourDetails,
        startDate: moment(yourDetails?.startDate).format("YYYY-MM-DD"),
      },
    };
    const coBorrowerDetailsData = {
      employmentType: others?.formTwo?.coBorrowerDetails?.employmentType,
      joiningDate: moment(
        others?.formTwo?.coBorrowerDetails?.joiningDate
      ).format("YYYY-MM-DD"),
      employerName: others?.formOne?.coBorrowerDetails?.employerName ?? "",
      ...(formTwoData?.coBorrowerDetails?.hassecondjob === "no" && {
        previousEmployer: {
          ...others?.formThree?.coBorrowerDetails,
          startDate: moment(
            others?.formThree?.coBorrowerDetails?.startDate
          ).format("YYYY-MM-DD"),
          endDate: moment(others?.formThree?.coBorrowerDetails?.endDate).format(
            "YYYY-MM-DD"
          ),
        },
      }),

      secondJob: {
        ...coBorrowerDetails,
        startDate: moment(coBorrowerDetails?.startDate).format("YYYY-MM-DD"),
      },
    };
    const savedYourDetailsData = {
      ...yourDetails,
      ...(yourDetails?.startDate && {
        startDate: moment(yourDetails.startDate).format("YYYY-MM-DD"),
      }),
    };

    if (yourDetailsData?.previousEmployer?.secondjob) {
      delete yourDetailsData.previousEmployer.secondjob;
    }
    if (coBorrowerDetailsData?.previousEmployer?.secondjob) {
      delete coBorrowerDetailsData.previousEmployer.secondjob;
    }

    const savedCoBorrowerDetailsData = {
      ...coBorrowerDetails,
      ...(coBorrowerDetails?.startDate && {
        startDate: moment(coBorrowerDetails.startDate).format("YYYY-MM-DD"),
      }),
    };
    const storedData = {
      yourDetails: savedYourDetailsData ?? {},
      coBorrowerDetails: savedCoBorrowerDetailsData ?? {},
    };
    dispatch(
      addStepsData({
        ...others,
        formFour: storedData,
      })
    );
    const request1 = {
      url: `${EMPLOYMENT}/${savedIds?.employmentID}/second`,
      data: yourDetailsData,
    };
    const request2 = {
      url: `${EMPLOYMENT}/${savedIds?.secondEmploymentID}/second`,
      data: coBorrowerDetailsData,
    };

    if (isYourSecondJob && isCoBorrowerSecondJob) {
      try {
        const [response1, response2] = await Promise.all([
          submitEmployment(request1),
          submitEmployment(request2),
        ]);
        if (response1?.success && response2?.success) {
          navigate(`/joint-income-details`);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Something went wrong.");
      }
    } else if (isYourSecondJob) {
      try {
        const [response1] = await Promise.all([submitEmployment(request1)]);
        if (response1?.success) {
          navigate(`/joint-income-details`);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Something went wrong.");
      }
    } else if (isCoBorrowerSecondJob) {
      try {
        const [response2] = await Promise.all([submitEmployment(request2)]);
        if (response2?.success) {
          navigate(`/joint-income-details`);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Something went wrong.");
      }
    }
  };

  useEffect(() => {
    if (formFour) {
      reset(formFour);
    }
  }, [formFour, reset]);

  useEffect(() => {
    const shouldClear =
      yourEmploymentType === "unemployed_or_retired" ||
      yourEmploymentType === "contractor" ||
      yourEmploymentType === "self-employed";
    if (shouldClear) {
      clearErrors([
        "yourDetails.startDate",
        "yourDetails.employerPhoneNumber",
        "yourDetails.postCode",
        "yourDetails.state",
        "yourDetails.street",
        "yourDetails.occupation",
      ]);
    }
  }, [yourEmploymentType]);

  useEffect(() => {
    const shouldClear =
      coEmploymentType === "unemployed_or_retired" ||
      coEmploymentType === "contractor" ||
      coEmploymentType === "self-employed";
    if (shouldClear) {
      clearErrors([
        "coBorrowerDetails.startDate",
        "coBorrowerDetails.employerPhoneNumber",
        "coBorrowerDetails.postCode",
        "coBorrowerDetails.state",
        "coBorrowerDetails.street",
        "coBorrowerDetails.occupation",
      ]);
    }
  }, [coEmploymentType]);

  const handlePrevious = () => {
    if (
      formTwoData?.coBorrowerDetails?.hassecondjob === "yes" ||
      formTwoData?.yourDetails?.hassecondjob === "yes"
    ) {
      dispatch(setStep(1));
    } else {
      dispatch(prevStep());
    }
  };
  return (
    <>
      <ProgressBar progress={100} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Second Employment Details" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex  flex-col md:flex-col xl:flex-row gap-3 sm:gap-4 ">
            <div className="flex-1">
              <InfoTitle title="Your Details" />

              {/* {(isYourPreviousEmployer || isYourSecondJob) && ( */}
              {isYourSecondJob && (
                <>
                  <DynamicSecondEmployment
                    control={control}
                    errors={errors}
                    register={register}
                    setValue={setValue}
                    setError={setError}
                    clearErrors={clearErrors}
                    occupation="yourDetails.occupation"
                    startDate="yourDetails.startDate"
                    employmentType="yourDetails.employmentType"
                    employerPhoneNumber="yourDetails.employerPhoneNumber"
                    addressStreetName="yourDetails.street"
                    addressStateName="yourDetails.state"
                    addressPostCodeName="yourDetails.postCode"
                    selectedEmploymentType={yourEmploymentType}
                  />
                </>
              )}

              {!isYourSecondJob && (
                <div className="text-center w-full flex justify-center z-50 align-center relative">
                  <DisabledSecondEmploymentDetails />
                </div>
              )}
            </div>
            <div className="flex-1">
              <InfoTitle title="Co-borrower" />
              {/* {(isCoBorrowerPreviousEmployer || isCoBorrowerSecondJob) && ( */}
              {isCoBorrowerSecondJob && (
                <>
                  <DynamicSecondEmployment
                    control={control}
                    errors={errors}
                    register={register}
                    setValue={setValue}
                    setError={setError}
                    clearErrors={clearErrors}
                    occupation="coBorrowerDetails.occupation"
                    startDate="coBorrowerDetails.startDate"
                    employmentType="coBorrowerDetails.employmentType"
                    employerPhoneNumber="coBorrowerDetails.employerPhoneNumber"
                    addressStreetName="coBorrowerDetails.street"
                    addressStateName="coBorrowerDetails.state"
                    addressPostCodeName="coBorrowerDetails.postCode"
                    selectedEmploymentType={coEmploymentType}
                  />
                </>
              )}

              {/* {!isCoBorrowerPreviousEmployer && !isCoBorrowerSecondJob && ( */}
              {!isCoBorrowerSecondJob && (
                <div className="text-center w-full flex justify-center z-50 align-center relative">
                  <DisabledSecondEmploymentDetails />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom onClick={handlePrevious}>Previous</PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default SecondEmployment;
